import { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Generic searchable select (combobox).
 *
 * Props:
 *   options     — array of { value: string|number, label: string }
 *   value       — currently selected value (string|number|null)
 *   onChange    — callback(value)
 *   placeholder — trigger placeholder text (default: "Select…")
 *   searchPlaceholder — input placeholder (default: "Search…")
 *   className   — extra classes on the trigger button
 *   testId      — data-testid for the trigger
 */
export function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  className,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selected = options.find(
    (o) => String(o.value) === String(value),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && (
              <CommandEmpty>No results found.</CommandEmpty>
            )}
            {filtered.map((o) => (
              <CommandItem
                key={o.value}
                value={String(o.value)}
                onSelect={() => {
                  onChange(o.value);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4 shrink-0",
                    String(value) === String(o.value) ? "opacity-100" : "opacity-0",
                  )}
                />
                {o.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
